import { useEffect } from "react";
import { Phone, Mail, MapPin, Instagram, Facebook, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import ContactForm from "@/components/ContactForm";
import Reveal from "@/components/Reveal";
import { COMPANY } from "@/data/site";

const Contact = () => {
  useEffect(() => {
    document.title = "Contact — Click World Online";
  }, []);
  return (
    <>
      <section className="relative bg-navy-deep grain overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-25" aria-hidden="true" />
        <div className="absolute -top-32 right-0 w-[500px] h-[400px] bg-brand/15 blur-[130px] rounded-full" aria-hidden="true" />
        <div className="relative max-w-8xl mx-auto px-6 lg:px-12 pt-40 pb-20 lg:pt-52 lg:pb-24">
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-electric font-medium mb-6">Contact</p>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              data-testid="contact-headline"
              className="font-display font-black tracking-tighter leading-[1.0] text-4xl sm:text-6xl lg:text-8xl text-mist max-w-5xl"
            >
              LET'S BUILD SOMETHING THAT <span className="text-electric">CLICKS.</span>
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-8 text-base md:text-lg text-slatec max-w-2xl leading-relaxed"
          >
            Tell us where your business is — and where you want it to go. We'll map the digital route together.
          </motion.p>
        </div>
      </section>

      <section className="bg-navy-deep" data-testid="contact-main-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 pb-24 lg:pb-36 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
          <div className="lg:col-span-5 flex flex-col gap-5">
            <Reveal>
              <div className="bg-navy-light border border-white/10 rounded-3xl p-8">
                <h2 className="font-display text-xl font-extrabold text-mist tracking-tight">Reach us directly</h2>
                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex gap-4">
                    <Phone size={18} className="text-electric shrink-0 mt-0.5" />
                    <span className="flex flex-col gap-1">
                      <a href="tel:+917307958742" data-testid="contact-phone-1" className="text-mist/85 hover:text-electric transition-colors">{COMPANY.phone1}</a>
                      <a href="tel:+917696650450" data-testid="contact-phone-2" className="text-mist/85 hover:text-electric transition-colors">{COMPANY.phone2}</a>
                    </span>
                  </li>
                  <li className="flex gap-4">
                    <Mail size={18} className="text-electric shrink-0 mt-0.5" />
                    <span className="flex flex-col gap-1">
                      <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="text-mist/85 hover:text-electric transition-colors">{COMPANY.email}</a>
                      <a href={`mailto:${COMPANY.email2}`} data-testid="contact-email-link-2" className="text-mist/85 hover:text-electric transition-colors">{COMPANY.email2}</a>
                    </span>
                  </li>
                  <li className="flex gap-4">
                    <MessageCircle size={18} className="text-electric shrink-0 mt-0.5" />
                    <a
                      href="https://wa.me/917307958742"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-link"
                      className="text-mist/85 hover:text-electric transition-colors"
                    >
                      WhatsApp us anytime
                    </a>
                  </li>
                  <li className="flex gap-4">
                    <MapPin size={18} className="text-electric shrink-0 mt-0.5" />
                    <span className="text-mist/85" data-testid="contact-address">
                      {COMPANY.name}<br />
                      {COMPANY.address1}<br />
                      {COMPANY.address2}
                    </span>
                  </li>
                </ul>
                <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-3">
                  <a href={COMPANY.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" aria-label="Instagram" className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-mist/70 hover:text-electric hover:border-electric/40 transition-colors">
                    <Instagram size={17} />
                  </a>
                  <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-link" aria-label="Facebook" className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-mist/70 hover:text-electric hover:border-electric/40 transition-colors">
                    <Facebook size={17} />
                  </a>
                  <span className="text-mist/40 text-xs ml-auto">{COMPANY.website}</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="bg-navy-light border border-white/10 rounded-3xl overflow-hidden">
                <iframe
                  src={COMPANY.mapsEmbed}
                  title="Click World Online location — Mansa, Punjab"
                  data-testid="contact-map"
                  className="w-full h-72 border-0"
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
              <a
                href={COMPANY.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-maps-link"
                className="mt-3 inline-flex items-center gap-1.5 text-electric text-sm font-semibold hover:gap-2.5 transition-[gap] duration-300"
              >
                <MapPin size={14} /> Open exact location in Google Maps
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
